import { flatten, omit, values } from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import {
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { BaseFilter } from "@circle-react-shared/MemberFilters/types";
import type { ProfileFieldBaseFilter } from "@circle-react-shared/MemberFilters/types";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

const INDEX_QUERY_KEY = ["internal_api", "community_bot", "agents"];

interface AgentResponse {
  count: number;
  page: number;
  per_page: number;
  records: Array<AgentProp>;
}

export interface AgentProp {
  allow_direct_messages: boolean;
  allow_search_conversations: boolean;
  answer_type: "ai-generated" | "custom";
  avatar_url: string;
  bio: object;
  created_at: string;
  enabled: boolean;
  id: number;
  members_filters?: Array<BaseFilter | ProfileFieldBaseFilter>;
  name: string;
  pause_ai_handoff_message: any;
  pause_ai_keywords: Array<string>;
  posts_count: number;
  purpose: string;
  send_welcome_message: boolean;
  snippets_count: number;
  tone: "normal" | "friendly" | "short-and-sweet" | "descriptive";
  updated_at: string;
  use_all_kb_posts: boolean;
  use_community_knowledge: boolean;
  use_custom_snippets: boolean;
  use_platform_support: boolean;
  welcome_message: object;
}

export interface UpdateAgentRequest {
  allow_direct_messages?: boolean;
  allow_search_conversations?: boolean;
  answer_type?: "ai-generated" | "custom";
  avatar?: string;
  bio?: object;
  enabled?: boolean;
  id?: number;
  members_filters?: Array<object>;
  name?: string;
  pause_ai_handoff_message?: any;
  pause_ai_keywords?: Array<string>;
  purpose?: string;
  send_welcome_message?: boolean;
  tone?: "normal" | "friendly" | "short-and-sweet" | "descriptive";
  welcome_message?: object;
}

export const useIndexAgents = ({
  currentPage,
  perPage = 10,
  enabled = true,
}: {
  currentPage: number;
  perPage?: number;
  enabled?: boolean;
}) =>
  useQuery<AgentResponse>({
    queryKey: [...INDEX_QUERY_KEY, currentPage, perPage],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.agents.index({
          params: {
            page: currentPage,
            per_page: perPage,
          },
        }),
      ),
    enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

export const useShowAgent = ({ id }: { id: number }) =>
  useQuery<AgentProp>({
    queryKey: [...INDEX_QUERY_KEY, id],
    queryFn: () => reactQueryGet(internalApi.communityBot.agents.show({ id })),
  });

export const useCreateAgent = () => {
  const { success } = useToast();

  return useMutation<AgentProp, Error, void>(
    async () => {
      const response: AgentProp = await reactQueryPost(
        internalApi.communityBot.agents.create(),
      );
      return response;
    },
    {
      onSuccess: () => {
        success(t("changes_saved"));
      },
    },
  );
};

export const useUpdateAgent = ({ id }: { id: number }) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();
  const { refetch: refetchPundit } = usePunditUserContext();

  return useMutation<AgentProp, Error, UpdateAgentRequest>(
    (data: UpdateAgentRequest) => {
      const isEnabledUpdate =
        Object.keys(data).length === 1 && "enabled" in data;

      return reactQueryPut(internalApi.communityBot.agents.update({ id }), {
        community_bot_profile: isEnabledUpdate
          ? { enabled: data.enabled }
          : omit(data, "enabled"),
      });
    },
    {
      onSuccess: data => {
        void queryClient.invalidateQueries([...INDEX_QUERY_KEY, data.id]);
        void queryClient.invalidateQueries([...INDEX_QUERY_KEY]);
        success(t("changes_saved"));
      },
      onError: async (exception: any) => {
        await refetchPundit();
        if (exception?.body?.error_details) {
          const details = values(
            omit(exception.body.error_details, "message", "success"),
          );
          error(flatten(details).join(", "));
        } else {
          error(t("request_failure_message"));
        }
      },
    },
  );
};
